import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { IoClose as Close } from 'react-icons/io5';
import { BsFilePdf as PdfIcon } from 'react-icons/bs';
import { useMediaQuery } from 'react-responsive';
import KzqTicketRegistryViewService from '@Helpers/URLHelpers/KaiziqResolve/views/KzqTicketRegistryViewService';
import ErrorHandler from '@Root/ErrorHandler';
import LabelViewOverViewChart from '@LabelAnalyticsDashboardsLabelViewCharts/LabelViewOverViewChart';
import LabelViewStatusOverView from '@LabelAnalyticsDashboardsLabelViewCharts/LabelViewStatusOverView';
import LabelViewCreatedOverTime from '@LabelAnalyticsDashboardsLabelViewCharts/LabelViewCreatedOverTime';
import KzqTicketRegistryList from '@LabelAnalyticsDashboardsLabelViewCharts/KzqTicketRegistryList';
import './LabelAnalytics.scss';

const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'usage',    label: 'Ticket Usage' },
];

export default function LabelAnalytics(props) {
    const [labelData] = useState({ ...props.labelData, registryType: props?.registryType });
    const [activeTab, setActiveTab]     = useState('overview');
    const [isExporting, setIsExporting] = useState(false);
    const is0To1200 = useMediaQuery({ maxWidth: 1200 });
    const className = 'labelAnalytics';

    const svc = new KzqTicketRegistryViewService();
    const errorHandler = new ErrorHandler();

    const registryLabel = (type) => {
        const map = { nameCard: 'Name Card', orgCard: 'Organization Card', labelCard: 'Label Card' };
        return map[type] || type;
    };

    const handlePdfExport = async () => {
        try {
            setIsExporting(true);
            const filterObj = {
                kzqTicketRegistryViewFilter: {
                    registryId:   labelData?.id,
                    registryType: labelData?.registryType,
                },
            };
            const data = await svc.exportPdf(filterObj);
            const outer = JSON.parse(data?.body);
            const res = JSON.parse(outer?.body);
            const raw = res?.file;
            if (!raw) return;
            const base64 = raw.startsWith('data:') ? raw.split(',')[1] : raw;
            const binary = atob(base64.replace(/\s/g, ''));
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            const blob = new Blob([bytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = res?.fileName || `${(labelData?.name || 'analytics').replace(/[^\w-]/g, '_')}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Modal
            open
            onClose={props?.handleLabelAnalytics}
            closeAfterTransition
            aria-labelledby="label-analytics-title"
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 200 }}
            className={`${className}__modal`}
        >
            <Fade in>
                <div className={className}>
                    <div className={`${className}__inner`} id="labelAnalyticsToExport">

                        {/* ── Header ─────────────────────────────────────── */}
                        <Box className={`${className}__header`}>
                            <Box className={`${className}__header__left`}>
                                <Box>
                                    <Typography className={`${className}__header__title`} id="label-analytics-title">
                                        Analytics Overview
                                    </Typography>
                                    <Box className={`${className}__header__meta`}>
                                        {labelData?.name && (
                                            <Chip size="small" label={labelData.name}
                                                className={`${className}__header__chip ${className}__header__chip--name`} />
                                        )}
                                        {labelData?.registryType && (
                                            <Chip size="small" variant="outlined" label={registryLabel(labelData.registryType)}
                                                className={`${className}__header__chip`} />
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                            <Box className={`${className}__header__actions`}>
                                {activeTab === 'overview' && (
                                    <>
                                        <Tooltip title={isExporting ? 'Generating PDF...' : 'Export as PDF'}>
                                            <span>
                                                <IconButton
                                                    onClick={handlePdfExport}
                                                    disabled={isExporting}
                                                    className={`${className}__header__btn`}
                                                    size="small"
                                                >
                                                    {isExporting
                                                        ? <CircularProgress size={14} thickness={4} />
                                                        : <PdfIcon style={{ color: '#e53935' }} />
                                                    }
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Divider orientation="vertical" flexItem className={`${className}__header__divider`} />
                                    </>
                                )}
                                <IconButton aria-label="Close analytics" onClick={props?.handleLabelAnalytics}
                                    className={`${className}__header__btn`} size="small">
                                    <Close />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* ── Tabs ───────────────────────────────────────── */}
                        <Box className={`${className}__tabs`}>
                            {TABS.map(tab => (
                                <button
                                    key={tab.id}
                                    className={`${className}__tab${activeTab === tab.id ? ' active' : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </Box>

                        {/* ── Body ───────────────────────────────────────── */}
                        <Box className={`${className}__body`} id="label-analytics-description">

                            {activeTab === 'overview' && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={is0To1200 ? 12 : 5}>
                                        <LabelViewOverViewChart labelData={labelData} />
                                    </Grid>
                                    <Grid item xs={12} md={is0To1200 ? 12 : 7}>
                                        <LabelViewStatusOverView labelData={labelData} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <LabelViewCreatedOverTime labelData={labelData} />
                                    </Grid>
                                </Grid>
                            )}

                            {activeTab === 'usage' && (
                                <KzqTicketRegistryList labelData={labelData} />
                            )}
                        </Box>
                    </div>

                </div>
            </Fade>
        </Modal>
    );
}
